import io from "socket.io-client";
import { API_BASE_URL } from "../../config/config";
import userEventConstructor from "../../utils/globals/userEventConstructor";
import callEventConstructor from "../../utils/globals/callEventConstructor";
import queryEventConstructor from "../../utils/globals/queryEventConstructor";
import mediaEventConstructor from "../../utils/globals/mediaEventConstructor";
import userStatus from "../../config/defaults/userStatus";
import callTypes from "../../config/defaults/callTypes";
import mediaStatus from "../../config/defaults/mediaStatus";
import callStatus from "../../config/defaults/callStatus";

// general events
const META_UPDATE = "meta-update";

const GeneralEmitters = (_socket) => {
  const updateMeta = (name, avatarFormula) => {
    _socket.emit(META_UPDATE, {
      name: name,
      avatarFormula: avatarFormula,
    });
  };

  return { updateMeta };
};

const CallEmitters = (_socket) => {
  const ignoreCall = (toId) => {
    _socket.emit(callEventConstructor({ status: callStatus.NOT_ANSWERED }), {
      toId: toId,
    });
  };

  const cancelCall = (toId) => {
    _socket.emit(callEventConstructor({ status: callStatus.CANCELED }), {
      toId: toId,
    });
  };

  const makeCall = (signal, fromId, fromName, fromAvatarFormula, toId) => {
    _socket.emit(callEventConstructor({ type: callTypes.OUTGOING }), {
      signal: signal,
      fromId: fromId,
      fromName: fromName,
      fromAvatarFormula: fromAvatarFormula,
      toId: toId,
    });
  };

  const acceptCall = (signal, toId) => {
    _socket.emit(callEventConstructor({ status: callStatus.ACCEPTED }), {
      signal: signal,
      toId: toId,
    });
  };

  const declineCall = (toId) => {
    _socket.emit(callEventConstructor({ status: callStatus.DECLINED }), {
      toId: toId,
    });
  };

  const completeCall = (toId) => {
    _socket.emit(callEventConstructor({ status: callStatus.COMPLETED }), {
      toId: toId,
    });
  };

  const updateCallMediaTrack = (toId, mediaState) => {
    _socket.emit(
      callEventConstructor({
        status: mediaEventConstructor({ status: mediaStatus.TRACK_CHANGE }),
      }),
      {
        toId: toId,
        mediaState,
      }
    );
  };

  return {
    ignoreCall,
    makeCall,
    cancelCall,
    acceptCall,
    declineCall,
    completeCall,
    updateCallMediaTrack,
  };
};

const UserListeners = (
  _socket,
  {
    onUserOffline = () => {},
    onUserOnline = () => {},
    onUserFound = () => {},
    onUserNotFound = () => {},
  }
) => {
  _socket.on(userEventConstructor({ status: userStatus.ONLINE }), (id) => {
    onUserOnline && onUserOnline(id);
  });

  _socket.on(callEventConstructor({ status: userStatus.OFFLINE }), ({ id }) => {
    onUserOffline && onUserOffline(id);
  });

  _socket.on(
    queryEventConstructor({
      acknowledged: userEventConstructor({ status: userStatus.FOUND }),
    }),
    ({ userId, name, avatarFormula }) => {
      onUserFound && onUserFound(userId, name, avatarFormula);
    }
  );

  _socket.on(
    queryEventConstructor({
      acknowledged: userEventConstructor({ status: userStatus.NOT_FOUND }),
    }),
    ({ userId }) => {
      onUserNotFound && onUserNotFound(userId);
    }
  );
};

const CallListeners = (
  _socket,
  {
    onIncommingCall = () => {},
    onCallCanceled = () => {},
    onCallCompleted = () => {},
    onCallAccepted = () => {},
    onCallDeclined = () => {},
    onCallIgnored = () => {},
    onCallMediaTrackUpdated = () => {},
  }
) => {
  _socket.on(
    callEventConstructor({ type: callTypes.INCOMMING }),
    ({ signal, fromId, fromName, fromAvatarFormula }) => {
      onIncommingCall &&
        onIncommingCall(signal, fromId, fromName, fromAvatarFormula);
    }
  );

  _socket.on(
    callEventConstructor({ status: callStatus.COMPLETED }),
    ({ signal }) => {
      onCallCompleted && onCallCompleted(signal);
    }
  );

  _socket.on(callEventConstructor({ status: callStatus.ACCEPTED }), (data) => {
    onCallAccepted && onCallAccepted(data?.signal);
  });

  _socket.on(
    callEventConstructor({ status: callStatus.DECLINED }),
    ({ fromId }) => {
      onCallDeclined && onCallDeclined(fromId);
    }
  );

  _socket.on(
    callEventConstructor({ status: callStatus.NOT_ANSWERED }),
    ({ fromId }) => {
      onCallIgnored && onCallIgnored(fromId);
    }
  );

  _socket.on(
    callEventConstructor({ status: callStatus.CANCELED }),
    ({ fromId }) => {
      onCallCanceled && onCallCanceled(fromId);
    }
  );

  _socket.on(
    callEventConstructor({
      status: mediaEventConstructor({ status: mediaStatus.TRACK_CHANGE }),
    }),
    ({ fromId, mediaState }) => {
      onCallMediaTrackUpdated && onCallMediaTrackUpdated(fromId, mediaState);
    }
  );
};

const VideoChatChannel = ({
  onUserOnline = () => {},
  onUserOffline = () => {},
  onUserFound = () => {},
  onUserNotFound = () => {},
  onIncommingCall = () => {},
  onCallCompleted = () => {},
  onCallAccepted = () => {},
  onCallDeclined = () => {},
  onCallIgnored = () => {},
  onCallCanceled = () => {},
  onCallMediaTrackUpdated = () => {},
}) => {
  const socket = io(API_BASE_URL);

  const { updateMeta } = GeneralEmitters(socket);

  const {
    makeCall,
    cancelCall,
    ignoreCall,
    declineCall,
    acceptCall,
    completeCall,
    updateCallMediaTrack,
  } = CallEmitters(socket);

  UserListeners(socket, {
    onUserOnline: onUserOnline,
    onUserOffline: onUserOffline,
    onUserFound: onUserFound,
    onUserNotFound: onUserNotFound,
  });

  CallListeners(socket, {
    onIncommingCall: onIncommingCall,
    onCallCanceled: onCallCanceled,
    onCallCompleted: onCallCompleted,
    onCallAccepted: onCallAccepted,
    onCallDeclined: onCallDeclined,
    onCallIgnored: onCallIgnored,
    onCallMediaTrackUpdated: onCallMediaTrackUpdated,
  });

  return {
    socket,
    updateMeta,
    makeCall,
    cancelCall,
    ignoreCall,
    acceptCall,
    declineCall,
    completeCall,
    updateCallMediaTrack,
  };
};

export default VideoChatChannel;
